import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectChannels } from '../store/selectors';
import { Modal } from 'Core/components';
import { archivePlan } from '../store/slice';

/** Value for `<input type="datetime-local" />` — local clock, minute precision. */
const formatDateTimeLocalValue = (date = new Date()) => {
  const p = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`;
};

export const ChannelDatePopup = ({ onClose, onSave, show, taskTitle, description }) => {
  const dispatch = useDispatch();
  const channels = useSelector(selectChannels);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [startDate, setStartDate] = useState(''); // stores full datetime-local value, e.g. 2025-02-19T10:30
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (show) {
      setStartDate(formatDateTimeLocalValue(new Date()));
    }
  }, [show]);

  useEffect(() => {
    setIsFormDirty(!!selectedChannel && !!startDate);
  }, [selectedChannel, startDate]);

  const handleArchive = async () => {
    if (!taskTitle) {
      return;
    }
    setArchiving(true);
    try {
      await dispatch(
        archivePlan({
          title: taskTitle,
          description: description || '',
        }),
      ).unwrap();
      onClose();
    } catch {
      // Rejection toasts handled in slice listener
    } finally {
      setArchiving(false);
    }
  };

  const handleSave = () => {
    onSave({ channel: selectedChannel, startDate });
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const modalContent = (
    <div className="container-fluid px-0">
      <div className="row">
        <div className="col-12">
          <div className="form-group mb-3">
            <label className="form-label required">Select Cell</label>
            <select
              className="form-select"
              value={selectedChannel}
              onChange={e => setSelectedChannel(e.target.value)}
            >
              <option value="">Select a cell...</option>
              {channels.map(channel => (
                <option key={channel.title} value={channel.uuid}>
                  {channel.title}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group mb-3">
            <label className="form-label required">Start Date</label>
            <input
              type="datetime-local"
              className="form-control"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const footer = () => {
    return (
      <div className="row">
        <div className="col-lg-4"></div>
        <div className="col-lg-8">
          <div className="col-lg-12">
            <div className="modal-footer-btn d-flex justify-content-end align-items-center">
              <button
                type="button"
                className="btn btn-secondary me-1"
                onClick={handleArchive}
                disabled={archiving || !taskTitle}
              >
                {archiving ? 'Archiving...' : 'Archived'}
              </button>
              <button
                type="button"
                className="btn btn-cancel me-1"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success me-1"
                onClick={handleSave}
                disabled={!isFormDirty}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      id="schedule-task-modal"
      show={show}
      onHide={onClose}
      title="Schedule task"
      body={modalContent}
      footer={footer}
      size="md"
      handleClose={handleClose}
    />
  );
};
